import React, { useState } from 'react'

import { useTheme } from '@mui/material/styles'

import 'chart.js/auto'
import { Chart } from 'react-chartjs-2'

import { useTranslation } from 'language/translator'
import ChartDataLabels from 'chartjs-plugin-datalabels'

import Box from 'components_new/atoms/Box'

import { getData, getOptions } from './utils'

import { translateWidgetType } from 'utils/enumTranslator'

import NoValue from '../../Errors/NoValue'
import SegmentMenu from '../../SegmentMenu'

import {
  FormattedWidgetData,
  isDashboardWidgetFullyLoaded,
  ParsedSegmentPath,
  ParsedWidgetDataLabel,
  TCoreWidgetFullyLoaded,
  WidgetType
} from 'types/GlobalWidget'

interface PieChartProps {
  customSegmentBy: string | null
  formattedData: FormattedWidgetData
  onSegmentClick?: (
    event: React.MouseEvent<HTMLElement, MouseEvent> | PointerEvent,
    segments: ParsedWidgetDataLabel[],
    datasetIndex?: number
  ) => void
  scaleFactor: number
  setCustomSegmentBy:
    | ((attributeOptionId: string, segment: ParsedSegmentPath[]) => void)
    | null
  setDetailsDialog: ((segment: ParsedSegmentPath[]) => void) | null
  setSegmentFilters: ((segment: ParsedSegmentPath[]) => void) | null
  widget: TCoreWidgetFullyLoaded
}

const PieChart = (props: PieChartProps) => {
  const { t } = useTranslation()
  const {
    customSegmentBy,
    formattedData,
    onSegmentClick,
    scaleFactor,
    setCustomSegmentBy,
    setDetailsDialog,
    setSegmentFilters,
    widget
  } = props

  const [anchorPosition, setAnchorPosition] = useState<{
    top: number
    left: number
  } | null>(null)
  const [selectedSegment, setSelectedSegment] =
    useState<ParsedWidgetDataLabel | null>(null)
  const allowOnClick = !!setSegmentFilters || !!setDetailsDialog

  const theme = useTheme()

  if (formattedData.datasets.length === 0) {
    return (
      <NoValue
        iconName={translateWidgetType[WidgetType.BAR_CHART].iconName}
        scaleFactor={scaleFactor}
      />
    )
  }

  const handleClick = (
    event: PointerEvent,
    dataLabel: ParsedWidgetDataLabel,
    datasetIndex?: number
  ) => {
    if (onSegmentClick) {
      onSegmentClick(event, [dataLabel], datasetIndex)

      return
    }

    event.stopPropagation()

    setAnchorPosition({ left: event.clientX, top: event.clientY })
    setSelectedSegment(dataLabel)
  }

  const handleClose = (event: React.MouseEvent<HTMLElement, MouseEvent>) => {
    event.stopPropagation()

    setAnchorPosition(null)
    setSelectedSegment(null)
  }

  const data = getData(theme, formattedData)

  return (
    <>
      <Box sx={{ position: 'relative', flexGrow: 1, minHeight: 0 }}>
        <Chart
          role="img"
          aria-label={widget.title || t('global', 'chart')}
          type="pie"
          data={data}
          options={getOptions(
            allowOnClick,
            data,
            handleClick,
            theme,
            scaleFactor,
            widget.settings.pie_chart_value_visualization_type
          )}
          plugins={[ChartDataLabels as any]}
        />
      </Box>
      {allowOnClick &&
        selectedSegment &&
        isDashboardWidgetFullyLoaded(widget) && (
        <SegmentMenu
          anchorPosition={anchorPosition}
          customSegmentBy={customSegmentBy}
          dashboardId={widget.dashboard_id}
          onClose={handleClose}
          segments={[selectedSegment]}
          setCustomSegmentBy={setCustomSegmentBy}
          setDetailsDialog={setDetailsDialog}
          setFilters={setSegmentFilters}
          widget={widget}
        />
      )}
    </>
  )
}

export default PieChart
